import { BackgroundCheckColumns, backgroundCheckEntityName } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { ExtendedAccountColumns } from '@/backend/features/contact-management/entities/extended-account/types';
import { ExtendedAccountFilterData } from '@/ui/features/contact-management/AccountsPanel/types';
import { AccountFilterParams, useAccountFilter } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountFilter';
import { useOverrideMapCredentialingAccountFilter } from '@juvare/credentialing/ui/hooks/useOverrideMapCredentialingAccountFilter';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Filter, FilterIdSubFilter } from '@juvare/platform-shared/features/storage';
import { useSelector } from 'react-redux';

export const useOverrideAccountFilter = (
    params: AccountFilterParams,
): {
    data?: Filter<ExtendedAccountColumns>[];
    isLoading: boolean;
    error?: unknown;
} => {
    const { data: accountData, error: accountError, isLoading: accountLoading } = useAccountFilter(params);

    const { useOverrideCredentialingAccountFilter } = useOverrideMapCredentialingAccountFilter();
    const settings = useSelector(getSettings);
    const {
        data: credentialingData,
        error: credentialingError,
        isLoading: credentialingLoading,
    } = useOverrideCredentialingAccountFilter(params);

    if (accountLoading || credentialingLoading) {
        return { data: undefined, isLoading: true, error: undefined };
    }

    if (accountError || credentialingError) {
        return { data: undefined, isLoading: false, error: accountError || credentialingError };
    }

    let combinedData: Filter<ExtendedAccountColumns>[] = [...((accountData ?? []) as Filter<ExtendedAccountColumns>[])];
    const combinedBackgroundCheckData: Filter<BackgroundCheckColumns>[] = [];

    const filterData = params.filterData as ExtendedAccountFilterData;
    if (filterData?.hospitalNameIds?.length) {
        combinedData.push({
            column: 'hospitalNameIds',
            op: 'overlap',
            value: filterData.hospitalNameIds,
        });
    }
    if (filterData?.allIssuingStatesOrJurisdictionsExceptCurrentClientCode) {
        combinedData.push({
            op: 'not',
            filter: [
                {
                    op: 'or',
                    filter: [
                        {
                            column: 'issuingStatesOrJurisdictions',
                            op: '=',
                            value: [settings.esarVhp.clientCode],
                        },
                        {
                            column: 'issuingStatesOrJurisdictions',
                            op: '=',
                            value: [],
                        },
                    ],
                },
            ],
        });
    } else if (filterData?.issuingStatesOrJurisdictions?.length) {
        combinedData.push({
            column: 'issuingStatesOrJurisdictions',
            op: 'overlap',
            value: filterData?.issuingStatesOrJurisdictions,
        });
    }

    if (filterData?.esarVhpId) {
        combinedData.push({ column: 'esarVhpId', op: '=', value: filterData.esarVhpId });
    }
    if (filterData?.stateOfWork) {
        combinedData.push({ column: 'stateOfWork', op: '=', value: filterData.stateOfWork });
    }

    if (filterData?.backgroundCheckType) {
        combinedBackgroundCheckData.push({
            column: 'type',
            op: '=',
            value: filterData.backgroundCheckType as BackgroundCheckType,
        });
    }

    if (filterData?.backgroundCheckStatus) {
        combinedBackgroundCheckData.push({
            column: 'status',
            op: 'in',
            value: filterData.backgroundCheckStatus,
        });
    }

    if (filterData?.backgroundCheckUpdatedAt?.from) {
        combinedBackgroundCheckData.push({
            column: 'updatedAt',
            op: '>=',
            value: Date.parse(filterData.backgroundCheckUpdatedAt.from),
        });
    }

    if (filterData?.backgroundCheckUpdatedAt?.to) {
        combinedBackgroundCheckData.push({
            column: 'updatedAt',
            op: '<',
            value: Date.parse(filterData.backgroundCheckUpdatedAt.to),
        });
    }

    if (combinedBackgroundCheckData.length > 0) {
        combinedData.push({
            op: 'in',
            idSubFilter: {
                entity: backgroundCheckEntityName,
                column: 'accountId',
                filter: combinedBackgroundCheckData,
            } satisfies FilterIdSubFilter<BackgroundCheckColumns>['idSubFilter'],
        });
    }

    if (filterData.occupations?.length) {
        combinedData.push({ column: 'occupationIds', op: 'overlap', value: filterData.occupations });
    }

    combinedData = [...combinedData, ...((credentialingData ?? []) as Filter<ExtendedAccountColumns>[])];

    return { data: combinedData, isLoading: false, error: undefined };
};
